import React from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../app/redux/useAppSelector';
import { FormConfirmationModal } from '../components/ConfirmationModal';
import { Field } from '../components/Form';
import { gql } from '../graphql/generated/gql';
import { groupToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import handleApolloError from '../utils/error/handleApolloError';
import useNavigate from '../utils/router/useNavigate';
import groupPath from '../utils/url/groupPath';

const CREATE_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupModalCreate($name: String!) {
    group: createGroup(name: $name) {
      ...groupPath_group
    }
  }
`);

const REQUEST_GROUP_MUTATION = gql(/** @complexity 2 */ `
  mutation GroupModalRequest($name: String!) {
    requestGroupCreation(name: $name)
  }
`);

interface GroupModalProps {
  close: () => void;
  canCreate: boolean;
}

type Values = {
  name: string;
};

const GroupModal = ({ close, canCreate }: GroupModalProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const initialValues = {
    name: '',
  };

  const [createGroupMutation] = useMutation(CREATE_GROUP_MUTATION);
  const [requestGroupMutation] = useMutation(REQUEST_GROUP_MUTATION);

  const handleSubmit = async (values: Values) => {
    if (canCreate) {
      try {
        const { data } = await createGroupMutation({
          variables: values,
        });

        const path = data?.group && groupPath(data.group);
        if (path) {
          navigate(path, { state: { edit: true } });
        }
      } catch (error) {
        handleApolloError(
          error,
          'FORBIDDEN',
          () => {
            dispatch(
              UIActions.pushToast(groupToasts.noPermissionToCreateGroupsError),
            );
          },
          () => {
            throw error;
          },
        );
      }
    } else {
      await requestGroupMutation({ variables: values });
      dispatch(
        UIActions.pushToast(groupToasts.groupCreationRequested, {
          duration: 3000,
        }),
      );
    }
  };

  return (
    <FormConfirmationModal<Values>
      form={{
        onSubmit: handleSubmit,
        initialValues,
        validateOnBlur: false,
      }}
      close={close}
      submitButton={{
        text: canCreate ? 'Create' : 'Request',
      }}
      title={canCreate ? 'Create new group' : 'Request new group'}
    >
      <Field
        autoFocus
        label="Name"
        name="name"
        required
        type="text"
        autoComplete="off"
      />
    </FormConfirmationModal>
  );
};

export default GroupModal;
